import { useEffect, useMemo, useState } from 'react'
import { getPlan, seedPlan, setTaskDone, type PlanTask } from '../../lib/ppc'
import { dateOfPlanDay, showtimeHeadline } from '../../../shared/show'

/**
 * Fifteen days to showtime.
 *
 * Five numbered stages, in the order the work actually happens, exactly as the
 * working site lays them out: a stage number set in Georgia, the days and who
 * owns them in spaced caps, then the checklist.
 *
 * Grouped by STAGE rather than by date. The banking tasks wait on other people
 * and the nursery tasks do not; a single date-ordered list makes a blocked bank
 * application look like a missed deadline.
 */
const STAGES = [
  { key: 'setup', no: '01', when: 'Days 1–2 · Q + Dillon', title: 'Set up the business' },
  { key: 'meet', no: '02', when: 'Days 3–7 · Dillon', title: 'Meet every plant' },
  { key: 'prepare', no: '03', when: 'Days 8–10 · Dillon + Q', title: 'Prepare the show' },
  { key: 'practice', no: '04', when: 'Days 11–13 · Dillon', title: 'Practice the whole sale' },
  { key: 'freeze', no: '05', when: 'Days 14–15 · Dillon', title: 'Freeze, pack, sell' },
] as const

export default function PlanScreen({ canWrite }: { canWrite: boolean }) {
  const [tasks, setTasks] = useState<PlanTask[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void getPlan().then((r) => setTasks(r.tasks)).catch(() => {}).finally(() => setLoading(false))
  }, [])

  /*
   * The show date is a CONSTANT, not a setting — see shared/show.ts. A deadline
   * that can be edited invites moving the deadline instead of doing the work,
   * which is the opposite of what a countdown is for.
   */
  const show = dateOfPlanDay(15)
  const shortDate = (at: Date) => at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

  const done = useMemo(() => tasks.filter((t) => t.done_at).length, [tasks])
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0

  async function set(id: string, value: boolean) {
    setTasks(await setTaskDone(id, value))
  }

  return (
    <>
      <div className="pagehead">
        <div>
          <p className="eyebrow">A small, confident first launch</p>
          <h1>{showtimeHeadline()}</h1>
          <p>
            One collection. One show. A few things done well.
            {show && <> Show day is <b>{show.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</b>.</>}
          </p>
        </div>
        {tasks.length > 0 && (
          <div className="actions">
            <span className="progress">
              <b>{done}</b>
              <span>/ {tasks.length} ready</span>
            </span>
          </div>
        )}
      </div>

      {tasks.length > 0 && (
        <div className="progress-bar"><i style={{ width: `${pct}%` }} /></div>
      )}

      {loading && <p className="muted">Loading…</p>}

      {!loading && tasks.length === 0 && (
        <div className="card empty">
          <h3>Fifteen days, five stages.</h3>
          <p>Set up the plan and the whole run-up to the show is a checklist.</p>
          {canWrite && (
            <button
              type="button" className="primary" disabled={busy}
              onClick={async () => { setBusy(true); setTasks(await seedPlan()); setBusy(false) }}
            >
              {busy ? 'Setting up…' : 'Set up the 15-day plan'}
            </button>
          )}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="stage-grid">
          {STAGES.map((stage) => {
            const mine = tasks.filter((t) => t.category === stage.key)
            if (mine.length === 0) return null
            return (
              <section className="card" key={stage.key}>
                <div className="stage">
                  <span className="stage-no" aria-hidden>{stage.no}</span>
                  <div className="stage-body">
                    <p className="stage-when">
                      {stage.when}
                      {(() => {
                        const first = dateOfPlanDay(Math.min(...mine.map((t) => t.day_offset)))
                        const last = dateOfPlanDay(Math.max(...mine.map((t) => t.day_offset)))
                        if (!first || !last) return null
                        return <> · {shortDate(first)}{shortDate(first) === shortDate(last) ? '' : `–${shortDate(last)}`}</>
                      })()}
                    </p>
                    <h3>{stage.title}</h3>
                    <ul className="task-list">
                      {mine.map((task) => (
                        <li key={task.id} className={task.done_at ? 'done' : undefined}>
                          <label className="checkbox">
                            <input
                              type="checkbox" checked={!!task.done_at} disabled={!canWrite}
                              onChange={(e) => void set(task.id, e.target.checked)}
                            />
                            <span>{task.title}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )
          })}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="note-panel">
          <h3>After the first show</h3>
          <p>
            Use what Dillon learns to add the public storefront, verified online ATH checkout,
            batch inventory and customer care profiles. Consider Android once the web workflow
            proves itself.
          </p>
          <p>
            <b>Launch gates:</b> Independent passkey access, payment and tax setup, and a
            rehearsal on Dillon’s phone. This private draft is not yet the public shop.
            Notifications stay controlled by Q.
          </p>
        </div>
      )}
    </>
  )
}
